/* eslint relay/unused-fields: "off" */
import { useId, FC, MouseEventHandler, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'dibs-react-intl';
import { handleLocaleUrl } from 'dibs-intl/exports/urls';
import CheckCircle from 'dibs-icons/exports/CheckCircle';
import dibsCss from 'dibs-css';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { SeoLink } from 'dibs-seo/exports/SeoLink';
import { SeoLink as SeoLinkElement } from 'dibs-elements/exports/SeoLink';
import { trackEvent } from 'dibs-tracking';
import ResponsiveImage from '../ResponsiveImage';
import classnames from 'classnames';
import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';
import {
    trackPromoClick,
    trackPromoImpressions,
    buildPromoTrackingObject,
} from '../helpers/promoTrackingHelpers';
import { trackModuleLocation, trackModule } from '../helpers/moduleLocationTracking';
import { useHomePageContext } from '../HomePageContext';
import { getAbTestV2 } from 'dibs-buyer-layout/exports/clientABTestV2';
import { useCoreBuyerIncentives } from 'dibs-buyer-incentives/exports/useCoreBuyerIncentives';

import styles from './HpSharedSkinnyBannerModule.scss';

import { HpSharedSkinnyBannerModule_componentModule$data } from './__generated__/HpSharedSkinnyBannerModule_componentModule.graphql';
import { HpSharedSkinnyBannerModule_user$data } from './__generated__/HpSharedSkinnyBannerModule_user.graphql';
import { HpSharedSkinnyBannerModule_viewer$data } from './__generated__/HpSharedSkinnyBannerModule_viewer.graphql';

type BannerMessages = NonNullable<
    NonNullable<HpSharedSkinnyBannerModule_componentModule$data['messages']>
>;
type LinkData = NonNullable<BannerMessages>['linkData'];

type Props = {
    viewer: HpSharedSkinnyBannerModule_viewer$data;
    user: HpSharedSkinnyBannerModule_user$data | null;
    componentModule: HpSharedSkinnyBannerModule_componentModule$data;
    useLazyLoadImages: boolean;
    moduleIndex: number;
    totalModules: number;
    isClient: boolean | null;
};

const buyerIncentivesCopies = [
    <FormattedMessage
        id="HpSharedSkinnyBannerModule.vettedSellers"
        defaultMessage="Vetted Professional Sellers"
    />,
    <FormattedMessage
        id="HpSharedSkinnyBannerModule.priceMatch"
        defaultMessage="Price-Match Guarantee"
    />,
    <FormattedMessage
        id="HpSharedSkinnyBannerModule.globalDelivery"
        defaultMessage="Trusted Global Delivery"
    />,
];

const HpSharedSkinnyBannerModule: FC<Props> = ({
    componentModule,
    useLazyLoadImages,
    moduleIndex,
    totalModules,
    viewer,
    user,
    isClient,
}) => {
    const { isMobile } = useHomePageContext();
    const htmlId = `skinnyBanner-${useId()}`;
    const intl = useIntl();

    const { messages, cmsDisplayTitle } = componentModule || {};

    const [isEnhancedBuyerIncentivesClientVariant, setIsEnhancedBuyerIncentivesClientVariant] =
        useState<boolean | null>(null);

    useEffect(() => {
        const isBuyerIncentivesVariantMobile =
            getAbTestV2('enhancedBuyerIncentives')?.variant === 'variant' && isMobile;
        const isBuyerIncentivesVariant1Resp =
            getAbTestV2('enhancedBuyerIncentivesCheckoutDesktop')?.variant === 'variant-1' &&
            !isMobile;
        const isBuyerIncentivesVariant2Resp =
            getAbTestV2('enhancedBuyerIncentivesCheckoutDesktop')?.variant === 'variant-2' &&
            !isMobile;

        setIsEnhancedBuyerIncentivesClientVariant(
            (isBuyerIncentivesVariantMobile ||
                isBuyerIncentivesVariant1Resp ||
                isBuyerIncentivesVariant2Resp) &&
                isClient
        );
    }, [isClient, isMobile]);

    const coreBuyerIncentives = useCoreBuyerIncentives({ viewer, item: null, user });

    if (!messages?.linkData || isEnhancedBuyerIncentivesClientVariant === null) {
        return null;
    }

    const imgSrc = messages?.imageUrl || messages?.mobileImageUrl || '';
    const imageCopy = messages?.imageCopy || undefined;
    const {
        isFullBleedImage,
        imageHeight,
        imageWidth,
        mobileImageHeight,
        mobileImageWidth,
        cta,
        linkData,
    } = messages || {};

    const promoTrackingObject = buildPromoTrackingObject({
        item: messages,
        id: 'skinny-banner',
        name: 'Skinny Banner',
    });

    const handleClick: MouseEventHandler = event => {
        if (isEnhancedBuyerIncentivesClientVariant) {
            trackEvent(
                {
                    category: 'promo interaction',
                    action: 'Learn More Click',
                    label: '[homepage]',
                },
                event
            );
        } else if (isEnhancedBuyerIncentivesClientVariant === false) {
            trackPromoClick(promoTrackingObject);
            trackModuleLocation({
                label: 'skinny banner module',
                moduleIndex,
                totalModules,
            });
            trackModule(cmsDisplayTitle);
        }
    };

    if (isEnhancedBuyerIncentivesClientVariant) {
        return (
            <div
                className={classnames(
                    styles.sectionWrapper,
                    dibsCss.textCenter,
                    dibsCss.bgShagreen300,
                    {
                        [dibsCss.mtLarger]: !isMobile,
                        [dibsCss.pXlarge]: !isMobile,

                        [dibsCss.mtLarge]: isMobile,
                        [dibsCss.ptMedium]: isMobile,
                        [dibsCss.pbSmall]: isMobile,
                        [dibsCss.pxSmall]: isMobile,
                    }
                )}
            >
                <div
                    className={classnames(dibsCss.mbXxsmall, {
                        [dibsCss.sassyFontHeaderSizeVinDiesel]: !isMobile,
                        [dibsCss.sassyFontHeaderSizeXLarge]: isMobile,
                    })}
                    data-tn="skinny-banner-title"
                >
                    <FormattedMessage
                        id="HpSharedSkinnyBannerModule.title"
                        defaultMessage="The 1stDibs Promise"
                    />
                </div>
                <div
                    className={classnames(dibsCss.sassyFontHeaderSizeMedium, dibsCss.mbMedium, {
                        [dibsCss.sassyFontHeaderSizeMedium]: !isMobile,
                        [dibsCss.mbMedium]: !isMobile,
                        [dibsCss.sassyFontHeaderSizeSmall]: isMobile,
                        [dibsCss.mbSmall]: isMobile,
                    })}
                >
                    <FormattedMessage
                        id="HpSharedSkinnyBannerModule.subTitle"
                        defaultMessage="We’re committed to your satisfaction and peace of mind with every purchase"
                    />
                </div>
                <div
                    className={classnames(styles.incentivesWrapper, dibsCss.grid, dibsCss.mbSmall, {
                        [dibsCss.grid]: !isMobile,
                        [dibsCss.gapSmall]: !isMobile,
                        [dibsCss.inlineGrid]: isMobile,
                        [dibsCss.gapXsmall]: isMobile,
                    })}
                >
                    {[...coreBuyerIncentives, ...buyerIncentivesCopies].map((copy, index) => (
                        <div
                            key={index}
                            className={classnames(dibsCss.flex, {
                                [dibsCss.sassyFontHeaderSizeLarge]: !isMobile,
                                [dibsCss.justifyCenter]: !isMobile,

                                [dibsCss.sassyFontHeaderSizeSmall]: isMobile,
                                [dibsCss.gapXsmall]: isMobile,
                                [dibsCss.itemsCenter]: isMobile,
                            })}
                        >
                            {isMobile && (
                                <span className={dibsCss.fillShagreen700}>
                                    <CheckCircle variation="20Filled" />
                                </span>
                            )}
                            {copy}
                        </div>
                    ))}
                </div>
                <div className={dibsCss.block}>
                    <SeoLinkElement
                        className={classnames({
                            [dibsCss.sassyFontBodyLarge]: !isMobile,

                            [dibsCss.sassyFontBodyMedium]: isMobile,
                            [dibsCss.inlineBlock]: isMobile,
                        })}
                        dataTn="skinny-banner-link"
                        linkData={{
                            path: handleLocaleUrl('/about/ourpromise/', intl.locale),
                            isLinkable: true,
                        }}
                        ariaLabel={cta || undefined}
                        ariaDescribedBy={imageCopy ? htmlId : undefined}
                        onClick={handleClick}
                        target="_blank"
                    >
                        {cta}
                    </SeoLinkElement>
                </div>
            </div>
        );
    }

    return (
        <div
            className={classnames(styles.wrapper, {
                [styles.isFullBleed]: isFullBleedImage,
            })}
        >
            {imageCopy && (
                <span id={htmlId} className={styles.imageCopy}>
                    {imageCopy}
                </span>
            )}
            <SeoLink
                className={styles.link}
                dataTn="skinny-banner-link"
                linkData={linkData as LinkData}
                ariaLabel={cta || undefined}
                ariaDescribedBy={imageCopy ? htmlId : undefined}
                onClick={handleClick}
            >
                <HpLazyImageWrapper
                    useLazyLoad={useLazyLoadImages}
                    imageHeight={imageHeight || mobileImageHeight}
                    imageWidth={imageWidth || mobileImageWidth}
                    onImageLoaded={() => trackPromoImpressions([promoTrackingObject])}
                >
                    <ResponsiveImage
                        ariaHidden
                        className={classnames(styles.image, {
                            [styles.isFullBleed]: isFullBleedImage,
                        })}
                        src={imgSrc}
                        srcSetWidths={[400, 640, 960, 1280, 1440]}
                    />
                </HpLazyImageWrapper>
            </SeoLink>
        </div>
    );
};

export default createFragmentContainer(HpSharedSkinnyBannerModule, {
    viewer: graphql`
        fragment HpSharedSkinnyBannerModule_viewer on Viewer {
            ...useCoreBuyerIncentives_viewer @arguments(userCountryCode: $userCountryCode)
        }
    `,
    user: graphql`
        fragment HpSharedSkinnyBannerModule_user on User {
            ...useCoreBuyerIncentives_user @arguments(isTrade: $isTrade)
        }
    `,
    componentModule: graphql`
        fragment HpSharedSkinnyBannerModule_componentModule on SkinnyBannerModule {
            cmsDisplayTitle
            messages(userType: $userType) {
                ... on SkinnyBannerModuleItemMessages @skip(if: $isMobile) {
                    imageUrl
                    imageHeight
                    imageWidth
                }
                ... on SkinnyBannerModuleItemMessages @include(if: $isMobile) {
                    mobileImageUrl
                    mobileImageHeight
                    mobileImageWidth
                }
                linkData {
                    path
                    ...SeoLink_linkData
                }
                cta
                imageCopy
                isFullBleedImage
            }
        }
    `,
});
